import type { UserManagerSettings } from 'oidc-client-ts';
import {
  ErrorResponse,
  UserManager,
  WebStorageStateStore,
} from 'oidc-client-ts';

export let isProd = ['app.tyro.school', 'new.tyro.school'].includes(
  window.location.hostname,
);

if (
  process.env.NODE_ENV === 'development' &&
  process.env.PUBLIC_AUTH_PROD === 'true'
) {
  isProd = true;
}

const authority = isProd
  ? 'https://auth.tyro.school/realms/tyro'
  : 'https://kc.tyro-dev.com/realms/tyro';

export const oidcConfig: UserManagerSettings = {
  authority,
  client_id: 'tyro-ui',
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

// OAuth/OIDC error codes that indicate the session is genuinely dead and
// re-authentication is required. Anything else (network blip, 5xx, etc.) is
// treated as transient — we keep the user where they are and let them retry.
const TERMINATING_OAUTH_ERRORS = new Set([
  'invalid_grant',
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
]);

export function isSessionTerminated(error: unknown): boolean {
  return (
    error instanceof ErrorResponse &&
    error.error !== null &&
    TERMINATING_OAUTH_ERRORS.has(error.error)
  );
}

// Sentry's default Breadcrumbs integration captures console.error. Raw errors
// from oidc-client-ts / fetch can carry request headers and bodies containing
// bearer tokens, so we strip down to a known-safe field set before logging.
export function sanitizeAuthError(error: unknown): Record<string, unknown> {
  if (error instanceof ErrorResponse) {
    return {
      name: error.name,
      message: error.message,
      error: error.error,
      error_description: error.error_description,
    };
  }
  if (error instanceof Error) {
    return { name: error.name, message: error.message };
  }
  return { message: String(error) };
}

async function handleRenewalFailure(
  um: UserManager,
  error: unknown,
  context: string,
): Promise<void> {
  console.error(`[auth] ${context} failed`, sanitizeAuthError(error));
  if (isSessionTerminated(error)) {
    await um.signinRedirect();
  }
}

// Proactively renew the token when it's expired or within this many seconds of
// expiry. Triggered by tab refocus (background timers may have missed their
// renewal window) and network recovery (offline periods can also miss it).
export const NEAR_EXPIRY_SECONDS = 60;

export async function renewIfStale(um: UserManager): Promise<void> {
  try {
    const user = await um.getUser();
    if (!user) return;
    const secondsUntilExpiry = user.expires_in ?? 0;
    if (!user.expired && secondsUntilExpiry > NEAR_EXPIRY_SECONDS) return;
    await um.signinSilent();
  } catch (error) {
    await handleRenewalFailure(um, error, 'proactive renewal');
  }
}

let userManager: UserManager | null = null;

export function getUserManager(): UserManager {
  if (!userManager) {
    const manager = new UserManager(oidcConfig);
    manager.events.addSilentRenewError((error) => {
      console.error('[auth] silent renew failed', sanitizeAuthError(error));
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void renewIfStale(manager);
      }
    });
    window.addEventListener('online', () => {
      void renewIfStale(manager);
    });
    userManager = manager;
  }
  return userManager;
}

export async function acquireAuthToken(): Promise<string | null> {
  const um = getUserManager();
  try {
    const user = await um.getUser();
    if (user && !user.expired) {
      return user.access_token;
    }
    const renewedUser = await um.signinSilent();
    return renewedUser?.access_token ?? null;
  } catch (error) {
    await handleRenewalFailure(um, error, 'token acquisition');
    return null;
  }
}
